import { useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { inputVariants } from '@/components/ui/Input';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { Callout, FieldLabel } from '@/features/local-mcp';
import {
  describeLogseqProblem,
  type LogseqConnection,
} from '@/features/local-mcp/logseq-setup';
import { useMcpServersQuery } from '@/features/settings/sub-settings/settings-local-mcp.query';

import { ConfigurationUnavailable } from './local-server-configuration';
import {
  useLogseqConfiguration,
  type LogseqStatus,
} from './use-logseq-configuration';

import type { ConfigurationContext } from './configuration.types';
import type { ServerEntry } from '@littlebird/axon-mcp';

const CARD = 'rounded-lg border border-border bg-surface';
const INPUT_CLASS = inputVariants({ inputSize: 'sm' });

function StatusCard({
  status,
  connection,
  onTestAgain,
}: {
  status: LogseqStatus;
  connection: LogseqConnection;
  onTestAgain: () => void;
}) {
  if (status.kind === 'checking') {
    return (
      <XStack className={`${CARD} items-center gap-2 p-3`}>
        <Icons.loader className="size-4 shrink-0 animate-spin text-foreground-muted" />
        <span className="text-sm text-foreground">Checking Logseq…</span>
      </XStack>
    );
  }
  if (status.kind === 'connected') {
    return (
      <XStack className={`${CARD} items-center gap-2.5 px-3 py-2.5`}>
        <span className="flex size-[22px] shrink-0 items-center justify-center rounded-full bg-success-3 text-success-11">
          <Icons.check className="size-3.5" />
        </span>
        <span className="flex-1 text-sm text-foreground">
          Connected to Logseq
        </span>
        <Button variant="ghost" size="sm" onClick={onTestAgain}>
          Test again
        </Button>
      </XStack>
    );
  }
  return (
    <Callout tone="danger" icon={<Icons.warn className="size-4" />}>
      <XStack className="items-start gap-3">
        <YStack className="min-w-0 flex-1 gap-0.5">
          <span className="text-sm font-medium text-foreground">
            Can’t reach Logseq
          </span>
          <span>
            {describeLogseqProblem(status.problem, connection, {
              inSetup: false,
            })}
          </span>
        </YStack>
        <Button
          variant="ghost"
          size="sm"
          className="shrink-0"
          onClick={onTestAgain}
        >
          Test again
        </Button>
      </XStack>
    </Callout>
  );
}

function TokenCard({
  token,
  saving,
  onSave,
}: {
  token: string;
  saving: boolean;
  onSave: (token: string, onSaved: () => void) => void;
}) {
  const [newToken, setNewToken] = useState<string | null>(null);

  if (newToken === null) {
    return (
      <XStack className={`${CARD} items-center gap-3 px-4 py-2.5`}>
        <Icons.key className="size-4 shrink-0 text-foreground-muted" />
        <span className="flex-1 font-mono text-sm text-foreground">
          {token ? `•••• ${token.slice(-4)}` : 'No token saved'}
        </span>
        <Button variant="outline" size="sm" onClick={() => setNewToken('')}>
          Replace token
        </Button>
      </XStack>
    );
  }

  return (
    <XStack className="gap-2">
      <input
        type="password"
        aria-label="New API token"
        placeholder="Paste your new Logseq API token"
        value={newToken}
        disabled={saving}
        onChange={(event) => setNewToken(event.target.value)}
        autoComplete="off"
        spellCheck={false}
        autoFocus
        className={INPUT_CLASS}
      />
      <Button
        variant="ghost"
        size="sm"
        className="h-8"
        disabled={saving}
        onClick={() => setNewToken(null)}
      >
        Cancel
      </Button>
      <Button
        variant="default"
        size="sm"
        className="h-8"
        disabled={!newToken.trim()}
        isLoading={saving}
        aria-label="Save token"
        onClick={() => onSave(newToken, () => setNewToken(null))}
      >
        Save
      </Button>
    </XStack>
  );
}

function AddressFields({
  connection,
  saving,
  onSave,
}: {
  connection: LogseqConnection;
  saving: boolean;
  onSave: (address: Pick<LogseqConnection, 'host' | 'port'>) => void;
}) {
  const [host, setHost] = useState(connection.host);
  const [port, setPort] = useState(connection.port);
  const changed =
    host.trim() !== connection.host || port.trim() !== connection.port;
  const canSave = changed && host.trim() !== '' && port.trim() !== '';

  return (
    <XStack className="items-end gap-3">
      <YStack className="flex-1 gap-1.5">
        <FieldLabel>Host</FieldLabel>
        <input
          aria-label="Host"
          value={host}
          disabled={saving}
          onChange={(event) => setHost(event.target.value)}
          spellCheck={false}
          className={INPUT_CLASS}
        />
      </YStack>
      <YStack className="w-28 gap-1.5">
        <FieldLabel>Port</FieldLabel>
        <input
          aria-label="Port"
          inputMode="numeric"
          value={port}
          disabled={saving}
          onChange={(event) => setPort(event.target.value)}
          spellCheck={false}
          className={INPUT_CLASS}
        />
      </YStack>
      <Button
        variant="outline"
        size="sm"
        className="h-8"
        disabled={!canSave}
        isLoading={saving}
        aria-label="Save host and port"
        onClick={() => onSave({ host, port })}
      >
        Save
      </Button>
    </XStack>
  );
}

function LogseqSettings({ server }: { server: ServerEntry }) {
  const { connection, status, saving, testAgain, saveConnection } =
    useLogseqConfiguration(server);

  return (
    <YStack className="gap-6 p-6">
      <StatusCard
        status={status}
        connection={connection}
        onTestAgain={testAgain}
      />
      <YStack className="gap-2">
        <FieldLabel>API token</FieldLabel>
        <TokenCard
          token={connection.token}
          saving={saving}
          onSave={(token, onSaved) => saveConnection({ token }, onSaved)}
        />
      </YStack>
      <AddressFields
        // Resets the draft whenever the saved address changes, so a stale
        // field can't overwrite a newer save
        key={`${connection.host}:${connection.port}`}
        connection={connection}
        saving={saving}
        onSave={(address) => saveConnection(address)}
      />
    </YStack>
  );
}

export function LogseqConfiguration({
  context,
}: {
  context: ConfigurationContext;
}) {
  const serverSlug =
    context.localSource?.kind === 'local_mcp'
      ? context.localSource.serverSlug
      : undefined;
  const serversQuery = useMcpServersQuery();
  const server = serversQuery.data?.find(
    (candidate) => candidate.slug === serverSlug
  );

  if (serversQuery.isPending) return null;
  if (serversQuery.isError || !server) {
    return (
      <ConfigurationUnavailable
        message="Couldn't read Logseq from Littlebird's local settings."
        onRetry={() => void serversQuery.refetch()}
      />
    );
  }

  return <LogseqSettings key={server.slug} server={server} />;
}
